import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { AuditFields, Page } from './types';

export interface Address {
  street: string;
  houseNumber: string;
  municipality: string;
  postalCode: string;
}

export interface Subject extends AuditFields {
  id: string;
  version: number;
  name: string;
  /** 9 digits, Register of schools and school facilities. */
  eduid: string;
  /** Code of the CVTI SR code list DRUHSK, e.g. "211". */
  type: string;
  address: Address;
  region: string;
  /** Code of the CVTI SR code list ICRKOKO in the region, e.g. "106"; optional. */
  district: string | null;
}

export interface SubjectRequest {
  name: string;
  eduid: string;
  type: string;
  address: Address;
  region: string;
  district: string | null;
  version?: number;
}

export interface SubjectQuery {
  q?: string;
  type?: string;
  region?: string;
  district?: string;
  page: number;
  size: number;
  sort: string;
}

export function useSubjects(query: SubjectQuery) {
  return useQuery({
    queryKey: ['subjects', query],
    queryFn: ({ signal }) => api<Page<Subject>>('/subjects', { query: { ...query }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useSubject(id: string | undefined) {
  return useQuery({
    queryKey: ['subjects', id],
    queryFn: () => api<Subject>(`/subjects/${id}`),
    enabled: Boolean(id),
  });
}

export function useSaveSubject(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: SubjectRequest) =>
      id
        ? api<Subject>(`/subjects/${id}`, { method: 'PUT', body: request, version: request.version })
        : api<Subject>('/subjects', { method: 'POST', body: request }),
    onSuccess: (subject) => {
      qc.setQueryData(['subjects', subject.id], subject);
      void qc.invalidateQueries({ queryKey: ['subjects'], exact: false });
    },
  });
}

export function useDeleteSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: { id: string; version: number }) => api<void>(`/subjects/${s.id}`, { method: 'DELETE', version: s.version }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['subjects'] }),
  });
}
